/**
 * ChatBox - Box principal de mensagens
 * Header + Messages Area + Input
 */

import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useConversation,
  useInfiniteMessages,  // ✅ Infinite scroll hook
  useSendMessage,
  useChatSocket,
  useOptimisticUpdates,
  useDragAndPaste,
  useFileAttachment,
  useAudioRecording,
  useMessageActions,
  useConversationActions,
  useMessageSearch,
  useContactTags,
  useNextConversationId,
  useMessagingWindow
} from '../hooks';
import { useToggleConversationMute } from '../hooks/useConversations';
import { sendMessageWithFile } from '../services/chatService';
import { chatDraftStore, type ComposerDraft } from '../services/chatDraftStore';
import { CHAT_ACCEPTED_FILE_PATTERNS } from '../utils/acceptedFileTypes';
import ChatInput from './ChatInput';
import type { ChatInputHandle } from './ChatInput';
import ChatMessage, { MessageAction } from './ChatMessage';
import DragOverlay from './DragOverlay';
import ConversationStageSelector from './ConversationStageSelector';
import ConversationContextStrip from './ConversationContextStrip';
import FilePreview from './FilePreview';
import AudioRecordingPreview from './AudioRecordingPreview';
import { isOfficialMetaMessagingProvider, type MessageContent, type QuickMessageContent, type QuickMessageResponse } from '@troia-v3/types';
import { useQuickMessages } from '@/modules/quick-messages/hooks/useQuickMessages';
import { quickMessagesService } from '@/modules/quick-messages/services/quickMessagesService';
import QuickMessagesDrawer from '@/modules/quick-messages/components/QuickMessagesDrawer';
import QuickMessageStagedPreview from '@/modules/quick-messages/components/QuickMessageStagedPreview';
import EditMessageDialog from './EditMessageDialog';
import ForwardMessageDialog from './ForwardMessageDialog';
import SendContactDialog from './SendContactDialog';
import SendPollDialog from './SendPollDialog';
import SendLocationDialog from './SendLocationDialog';
import DeleteMessageDialog from './DeleteMessageDialog';
import ReplyPreview from './ReplyPreview';
import { TransferConversationDialog } from '@/shared/components/dialogs/TransferConversationDialog';
import FinishChatDialog from './FinishChatDialog';
import MessageSearch from './MessageSearch';
import ImageViewer from './ImageViewer';
import DateSeparator, { isSameDay, getMessageDate } from './DateSeparator';
import { toast } from 'sonner';

import { useSocket } from '@/contexts/SocketContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/lib/api';
import type { ConversationMessageResponse } from '@troia-v3/types';
import {
  SOCKET_EVENTS,
  ProviderCapability,
  MESSAGING_WINDOW_CLOSED_CODE,
  CONTACT_BLOCKED_CODE,
  type ConversationOpenEvent,
  type ConversationUnreadResetEvent,
  type ConversationErrorEvent
} from '@troia-v3/types';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/Button';
import { CardActionsMenu, Tooltip, type CardAction } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { formatPhoneNumber } from '@/shared/utils';
import { LeadFormDrawer } from '@/modules/crm/leads/components';
import { EventFormModal } from '@/modules/calendar/components/EventFormModal';
import { useUserCalendarIntegrations } from '@/modules/calendar/hooks';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { usePermissions } from '@/hooks/usePermissions';
import ConversationCustomerSelector from './ConversationCustomerSelector';
import TagsDialog from './TagsDialog';
import { Icon, IntegrationIcon } from '@/shared/icons'
import { PrivacyText } from '@/shared/components/ui/PrivacyText'
import { ConversationDetailsModal } from './ConversationDetailsModal'
import { SendTemplateModal } from './SendTemplateModal';
import { MessagingWindowBlock } from './MessagingWindowBlock';
import { MessagingWindowWarning } from './MessagingWindowWarning';
import { ContactBlockedBlock } from './ContactBlockedBlock';
import { useIsMobile } from '@/shared/hooks';
import { useBlockContact, useUnblockContact } from '@/modules/contacts/hooks/useContactMutations';
import {
  getGoldenStatus,
  markConversationAsGolden,
  unmarkConversationAsGolden,
} from '../services/agentGoldenCasesApi';

/**
 * Largura do CONTENT BOX do header (px) em que silenciar / informações / fechar saem da
 * barra e passam a existir só no menu `···` — primeiro degrau da cadeia (spec §4).
 * O MESMO número está em `src/index.css` (`@container chatheader (max-width: 936px)`).
 * São 936 e não os 964 do mockup porque 964 é a largura da CONVERSA e o header tem
 * `padding: 0 14px`: tanto o `@container` quanto o `ResizeObserver.contentRect` medem o
 * content box, 28px menor. Mexeu num, mexa no outro: o CSS esconde o botão da barra,
 * este número monta a ação no menu.
 */
const CHAT_HEADER_DEMOTE_AT = 936;

interface ChatBoxProps {
  conversationId: string;
  onToggleRightSidebar: () => void;
  rightSidebarOpen: boolean;
  onClose: () => void;
  onSelectConversation?: (nextId: string) => void;
  /** Termo de busca inicial vindo da sidebar - quando fornecido, abre automaticamente a busca */
  initialSearchTerm?: string;
}

const ChatBox: React.FC<ChatBoxProps> = ({
  conversationId,
  onToggleRightSidebar,
  rightSidebarOpen,
  onClose,
  onSelectConversation,
  initialSearchTerm,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null); // ✅ Ref para container de scroll
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // Largura do header pra decidir o que vai pro menu `···` (o CSS decide o que some
  // da barra; aqui decidimos o que ENTRA no menu, senão a ação sumia calada).
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerWidth, setHeaderWidth] = useState(0);
  const [editingMessage, setEditingMessage] = useState<ConversationMessageResponse | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [forwardingMessage, setForwardingMessage] = useState<ConversationMessageResponse | null>(null);
  const [isForwardDialogOpen, setIsForwardDialogOpen] = useState(false);
  // Pickers do menu de anexos (spec 2026-08-19 — Fase 3: localização/contato)
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [isPollCreatorOpen, setIsPollCreatorOpen] = useState(false);
  const [isSendingContent, setIsSendingContent] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState<ConversationMessageResponse | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<ConversationMessageResponse | null>(null);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const [isFinishDialogOpen, setIsFinishDialogOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const isMobile = useIsMobile();
  const { hasModulePermission } = usePermissions();
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false);
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [isSendTemplateOpen, setIsSendTemplateOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  // Qual aba a modal de detalhes abre (individual → 'privacy'; grupo → 'privacy' ou 'members')
  const [detailsOnlyTab, setDetailsOnlyTab] = useState<'privacy' | 'members'>('privacy');

  // Usuário atual
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();

  const { state: authState } = useAuth();
  const currentUserId = authState.user?.id;

  // Buscar dados da conversa
  const { data: conversation, isLoading: conversationLoading } = useConversation(conversationId);

  // Canal da conversa + suporte a templates (ProviderCapability.TEMPLATE_MANAGEMENT)
  // ConversationResponse não inclui capabilities diretamente — buscar via channels/dropdown.
  const conversationChannelId = conversation?.channelId;
  const { data: channelsDropdown = [] } = useChannelsDropdown();
  const channelSupportsTemplates = (() => {
    if (!conversationChannelId) return false;
    const ch = channelsDropdown.find((c) => c.id === conversationChannelId);
    return ch?.capabilities.includes(ProviderCapability.TEMPLATE_MANAGEMENT) ?? false;
  })();

  // Editar mensagem enviada NÃO existe na WhatsApp Cloud API (canal oficial
  // Meta) — não há endpoint na Graph API. Oferecer o botão ali prometia algo
  // que falhava depois em silêncio, deixando o atendente vendo um texto que o
  // cliente nunca recebeu (bug de produção 2026-08-24; o backend agora recusa
  // com 422 e esta checagem evita o caminho de erro na UI).
  // Enquete só existe em canal conectado por QR Code — a WhatsApp Cloud API
  // (canal oficial Meta) não tem enquete entre os tipos interativos.
  const channelSupportsPoll = (() => {
    if (!conversationChannelId) return false; // canal indeterminado: não oferecer
    const ch = channelsDropdown.find((c) => c.id === conversationChannelId);
    return !isOfficialMetaMessagingProvider(ch?.providerId);
  })();

  const channelSupportsEdit = (() => {
    if (!conversationChannelId) return true; // canal indeterminado: não bloquear
    const ch = channelsDropdown.find((c) => c.id === conversationChannelId);
    return !isOfficialMetaMessagingProvider(ch?.providerId);
  })();

  // Buscar tags do contato (apenas se não for grupo). O `|| contact?.id` é o mesmo
  // fallback do ConversationStageSelector/CustomerSelector/ContextStrip — sem ele a
  // coluna do header ficava vazia (e o gatilho desabilitado) quando o FK `contactId`
  // não vinha populado, enquanto a faixa do celular mostrava as tags de verdade.
  const headerContactId = conversation?.contactId || conversation?.contact?.id;
  const { data: contactTags = [] } = useContactTags(
    conversation?.conversationType !== 'group' ? headerContactId : undefined
  );

  // Golden Cases — marcar conversa como exemplo (só conversas do tipo 'ai')
  const hasAgent = !!conversation?.agentId;
  const { data: goldenStatus, refetch: refetchGolden } = useQuery({
    queryKey: ['goldenStatus', conversationId],
    queryFn: () => getGoldenStatus(conversationId),
    enabled: hasAgent && !!conversationId,
  });

  const markGoldenMutation = useMutation({
    mutationFn: () => markConversationAsGolden(conversationId, conversation?.agentId),
    onSuccess: () => {
      void refetchGolden();
      toast.success('Conversa salva como exemplo');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao salvar conversa como exemplo');
    },
  });
  const unmarkGoldenMutation = useMutation({
    mutationFn: () => unmarkConversationAsGolden(conversationId),
    onSuccess: () => {
      void refetchGolden();
      toast.success('Conversa removida dos exemplos');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Erro ao desmarcar conversa');
    },
  });

  const isGolden = goldenStatus?.isGolden ?? false;
  const toggleGolden = (): void => {
    if (isGolden) unmarkGoldenMutation.mutate();
    else markGoldenMutation.mutate();
  };

  // ✅ Verificar se conversa está em atendimento pelo STATUS
  const isInAttendance = conversation?.status === 'active';

  // ✅ Verificar se usuário pode iniciar atendimento: atendente do canal
  // (userRole do backend) OU o próprio responsável pela conversa, desde que
  // vinculado ao canal (userRole 'viewer') — D12, mesma regra do backend em
  // `startAttendance`. Quem foi removido do canal (sem userRole) não atende.
  const canStartAttendance =
    conversation?.userRole === 'attendant' ||
    (conversation?.userRole === 'viewer' &&
      currentUserId !== undefined &&
      conversation.assigneeId === currentUserId);

  // `contentRect` = content box, a MESMA medida que o `@container` do `src/index.css`
  // enxerga (o header tem padding lateral). Nunca trocar por `borderBoxSize`: CSS e JS
  // passariam a discordar em 28px e o botão sumiria da barra sem entrar no menu.
  // A dependência é `conversationLoading` porque enquanto a conversa carrega o header
  // não está montado e o ref é `null`.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      setHeaderWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [conversationLoading, conversationId]);

  // ✅ Atalho Delete / Cmd+Backspace: abre modal de finalizar conversa (apenas quando em atendimento)
  useEffect(() => {
    const handleDeleteKey = (e: KeyboardEvent) => {
      const isDelete = e.key === 'Delete';
      const isCmdBackspace = e.key === 'Backspace' && e.metaKey;
      if (!isDelete && !isCmdBackspace) return;

      // Ignorar quando foco está em input/textarea/select
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      if (isInAttendance && !isFinishDialogOpen) {
        e.preventDefault();
        setIsFinishDialogOpen(true);
      }
    };

    window.addEventListener('keydown', handleDeleteKey);
    return () => window.removeEventListener('keydown', handleDeleteKey);
  }, [isInAttendance, isFinishDialogOpen]);

  // Event creation
  const { data: calendarIntegrations = [] } = useUserCalendarIntegrations();

  // Mutation para iniciar atendimento
  const startAttendanceMutation = useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await api.post(`/conversations/${conversationId}/start`);
      return response.data;
    },
    onSuccess: (_data) => {
      // ✅ Update otimista: atualizar cache da conversa atual
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            agentStatus: 'paused',           // ✅ IA pausada, agente assumiu
            status: 'active',                // ✅ Status da conversa: active
            conversationType: 'individual',  // ✅ Muda para individual para aparecer na tab "Chats"
          }
        };
      });

      // ✅ Remover conversa da lista de conversas IA (infinite scroll)
      queryClient.setQueriesData(
        { queryKey: ['conversations', 'infinite', 'ai'] },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.filter((conv: any) => conv.id !== conversationId),
              total: page.total - 1, // Decrementa o total
            })),
          };
        }
      );

      // ✅ Invalidar queries específicas para refetch
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations', 'infinite', 'individual'] }); // Refetch lista de chats
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false }); // Refetch geral

      toast.success('Atendimento iniciado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar atendimento');
    }
  });

  const handleStartAttendance = () => {
    startAttendanceMutation.mutate(conversationId);
  };

  // Mutation para toggle AI agent status
  const toggleAgentStatusMutation = useMutation({
    mutationFn: async ({ conversationId, newStatus }: { conversationId: string; newStatus: 'active' | 'paused' }) => {
      const response = await api.patch(`conversations/${conversationId}`, {
        agentStatus: newStatus
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // ✅ Update otimista: atualizar cache da conversa atual
      queryClient.setQueryData(['conversation', conversationId], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            agentStatus: variables.newStatus,
          }
        };
      });

      // ✅ Invalidar queries para refetch
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });

      toast.success(
        variables.newStatus === 'active'
          ? 'Agente de IA ativado com sucesso!'
          : 'Agente de IA pausado com sucesso!'
      );
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar status do agente de IA');
    }
  });

  const handleToggleAgentStatus = () => {
    if (!conversation) return;

    const newStatus = conversation.agentStatus === 'active' ? 'paused' : 'active';
    toggleAgentStatusMutation.mutate({ conversationId, newStatus });
  };

  // ✅ Buscar mensagens com infinite scroll
  const {
    data: messagesData,
    isLoading: messagesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteMessages({
    conversationId,
    limit: 70
  });

  // ✅ Flatten de todas as páginas em um array único
  // Backend retorna em ordem descendente (newest first)
  const messages = messagesData?.pages.flatMap((page: any) => page.items) ?? [];

  // ✅ Inverter para exibição em chat (oldest first no topo, newest no fundo)
  // Backend: [30, 29, 28...6] → Frontend: [6, 7, 8...30]
  const displayMessages = [...messages].reverse();

  // Janela de mensageria da Meta (24h) → estado do composer (Task 12/13).
  // `messages` vem do backend em ordem DESCENDENTE (newest first) — a mais
  // recente pela ordem de renderização do componente é o último item de
  // `displayMessages` (oldest→newest), não `messages[messages.length - 1]`
  // (que seria a mais antiga carregada). Alimenta a reconciliação: se a Meta
  // recusou por janela, o hook força `window_closed` mesmo que o relógio
  // local discorde.
  const lastMessage = displayMessages[displayMessages.length - 1];
  const { state: composerState, reopenWith, showWarning, remainingLabel } = useMessagingWindow(conversation, lastMessage);

  // Extrair todas as imagens das mensagens para o ImageViewer
  const allImages = messages
    .filter(msg => msg.content.some((c: any) => c.type === 'image' && c.url))
    .map(msg => {
      const imageContent = msg.content.find((c: any) => c.type === 'image');
      return {
        id: msg.id,
        url: imageContent.url,
        caption: imageContent.caption || '',
        sentAt: msg.sentAt,
        senderName: msg.senderName || 'Desconhecido'
      };
    });

  // Handler para abrir ImageViewer
  const handleImageClick = (messageId: string) => {
    const imageIndex = allImages.findIndex(img => img.id === messageId);
    if (imageIndex !== -1) {
      setSelectedImageIndex(imageIndex);
      setIsImageViewerOpen(true);
    }
  };

  // Mutation para enviar mensagem
  const sendMessageMutation = useSendMessage();

  // Optimistic updates
  const {
    addOptimisticMessage,
    updateOptimisticMessage,
    markOptimisticMessageFailed,
  } = useOptimisticUpdates();

  // Drag & drop de arquivos
  const { isDragging, files: draggedFiles, clearFiles: clearDraggedFiles } = useDragAndPaste({
    targetRef: chatContainerRef,
    accept: CHAT_ACCEPTED_FILE_PATTERNS,
    maxFileSize: 100 * 1024 * 1024, // 100MB (teto físico; limite real por canal validado no backend)
    maxFiles: 10,
    enabled: true,
  });

  // Gestão de anexos
  const {
    attachments,
    addAttachments,
    removeAttachment,
    clearAttachments,
    restoreAttachments,
    uploadAttachment,
    formatFileSize,
    isUploading,
  } = useFileAttachment({
    uploadFn: async (file, onProgress, caption, replyToMessageId) => {
      // Importar sendMessageWithFile do chatService
      const message = await sendMessageWithFile(
        conversationId,
        file,
        caption, // ✅ legenda digitada no input acompanha a mídia
        replyToMessageId, // ✅ vínculo de resposta acompanha a mídia
        onProgress
      );

      // ✅ CORREÇÃO: Retornar mensagem completa do servidor (não apenas URL)
      // Isso permite que updateOptimisticMessage funcione corretamente
      return message;
    },
  });

  // Gravação de áudio
  const {
    isRecording,
    // isPaused,
    duration,
    audioData,
    startRecording,
    stopRecording,
    // pauseRecording,
    // resumeRecording,
    cancelRecording,
    clearAudio,
    restoreAudio,
    formatDuration,
  } = useAudioRecording({
    onRecordingComplete: async (blob) => {
      // Audio será enviado quando usuário clicar em "Enviar"
      console.log(blob);
    },
  });

  // Mensagens rápidas (quick messages)
  const chatInputRef = useRef<ChatInputHandle>(null);
  const [isQuickMessagesOpen, setIsQuickMessagesOpen] = useState(false);
  const [stagedQuickMessageMedia, setStagedQuickMessageMedia] = useState<QuickMessageContent[] | null>(null);

  // ── Rascunho por conversa (chatDraftStore) ─────────────────────────────
  // Snapshot sempre-fresco do composer para o stash de unmount (voltar ao
  // dashboard). Atualizado a cada render; declarado ANTES dos efeitos de
  // restore para a ordem de execução preservar os valores corretos.
  const composerSnapshotRef = useRef<ComposerDraft>({
    attachments: [],
    audioData: null,
    replyingToMessage: null,
    stagedQuickMessageMedia: null,
  });
  useEffect(() => {
    composerSnapshotRef.current = { attachments, audioData, replyingToMessage, stagedQuickMessageMedia };
  });

  const conversationIdRef = useRef(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  });

  // Mount: restaura o rascunho de mídia; unmount (dashboard): stash.
  // snapshotRef recebe o draft SÍNCRONO no restore para o ciclo do StrictMode
  // (mount→unmount→mount em dev) re-stashar o rascunho em vez de apagá-lo.
  useEffect(() => {
    const draft = chatDraftStore.getComposer(conversationId);
    if (draft) {
      restoreAttachments(draft.attachments);
      restoreAudio(draft.audioData);
      setReplyingToMessage(draft.replyingToMessage);
      setStagedQuickMessageMedia(draft.stagedQuickMessageMedia);
      composerSnapshotRef.current = draft;
    }
    return () => {
      chatDraftStore.setComposer(conversationIdRef.current, composerSnapshotRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Troca de conversa: stash da anterior (a closure deste render ainda tem os
  // valores dela — nenhum reset rodou) + restore da nova. Corrige o vazamento
  // histórico de anexos/áudio/reply entre conversas. Gravação EM ANDAMENTO é
  // descartada (parar o recorder é assíncrono — race com o stash; decisão da spec).
  const prevConversationIdRef = useRef(conversationId);
  useEffect(() => {
    const prevId = prevConversationIdRef.current;
    if (prevId === conversationId) return;
    prevConversationIdRef.current = conversationId;

    if (isRecording) cancelRecording();
    chatDraftStore.setComposer(prevId, {
      attachments,
      audioData,
      replyingToMessage,
      stagedQuickMessageMedia,
    });

    const draft = chatDraftStore.getComposer(conversationId);
    restoreAttachments(draft?.attachments ?? []);
    restoreAudio(draft?.audioData ?? null);
    setReplyingToMessage(draft?.replyingToMessage ?? null);
    setStagedQuickMessageMedia(draft?.stagedQuickMessageMedia ?? null);
    composerSnapshotRef.current = draft ?? {
      attachments: [],
      audioData: null,
      replyingToMessage: null,
      stagedQuickMessageMedia: null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const { data: quickMessagesData } = useQuickMessages(undefined, isInAttendance);
  const quickMessages = quickMessagesData?.items ?? [];

  /**
   * Insere uma mensagem rápida no composer: texto vai pro campo, mídia fica
   * "staged" no preview para revisão antes de enviar (NUNCA reenvia upload —
   * usa a URL S3 já hospedada via /send).
   */
  const handleInsertQuickMessage = useCallback((qm: QuickMessageResponse) => {
    const textPart = qm.content.find((c) => c.type === 'text');
    const text = textPart && textPart.type === 'text' ? textPart.text : '';
    const media = qm.content.filter((c) => c.type !== 'text');

    chatInputRef.current?.setText(text);
    setStagedQuickMessageMedia(media.length > 0 ? media : null);
    chatInputRef.current?.focus();

    void (async () => {
      try {
        await quickMessagesService.registerUsage(qm.id);
      } catch {
        // best-effort usage tracking
      }
    })();
  }, []);

  // WebSocket para mensagens em tempo real
  // `composerState` (B10, review 2026-07-27): já calculado acima por
  // `useMessagingWindow` (linha ~341) — passado aqui em vez de recalculado
  // dentro do hook de socket, que só usa pra decidir SE invalida
  // `['conversation', id]` num inbound (nunca pra decidir se a mensagem entra
  // no cache, isso continua incondicional).
  useChatSocket({
    conversationId,
    composerState,
    onNewMessage: (_message) => {
      // React Query vai invalidar e refetch automaticamente
    },
    onMessageUpdate: (_message) => {
    },
    enabled: true,
  });

  // Message actions (edit, delete, react, forward)
  const {
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    forwardMessageAsync,
    isEditingMessage,
    isDeletingMessage,
  } = useMessageActions({
    conversationId,
    onEditSuccess: () => {
      setIsEditDialogOpen(false);
      setEditingMessage(null);
    },
    onDeleteSuccess: (messageId) => {
      // Mensagem excluída com sucesso
      console.log(messageId);
    },
  });

  // Message search
  const {
    setSearchQuery,
    totalResults,
    currentResultIndex,
    navigateNext,
    navigatePrev,
    highlightedMessageId,
  } = useMessageSearch({
    messages,  // ✅ Usando array flat de todas as páginas
    isEnabled: isSearchOpen,
  });

  // ✅ Auto-abrir busca quando initialSearchTerm é fornecido (vindo da sidebar)
  useEffect(() => {
    if (initialSearchTerm && initialSearchTerm.trim()) {
      setIsSearchOpen(true);
      setSearchQuery(initialSearchTerm.trim());
    }
  }, [initialSearchTerm, setSearchQuery]);

  const getNextConversationId = useNextConversationId();

  // Conversation actions (transfer, close, reopen)
  const {
    transferConversation,
    closeConversation,
    isTransferring,
    isClosing,
  } = useConversationActions({
    conversationId,
    onTransferSuccess: () => {
      setIsTransferDialogOpen(false);
    },
    onCloseSuccess: () => {
      setIsFinishDialogOpen(false);
      const nextId = getNextConversationId(conversationId);
      if (nextId && onSelectConversation) {
        onSelectConversation(nextId);
      } else {
        onClose();
      }
    },
  });

  // Mute sincronizado com backend — `conversation.muted` é boolean computado
  // per-usuário no mapper; mutation chama POST /mute ou /unmute conforme estado.
  const isConversationMuted = conversation?.muted === true;
  const { mutate: toggleConversationMute, isPending: isMuteToggling } = useToggleConversationMute();

  // Bloqueio de contato (2026-09-01) — kebab do individual (I3: grupo nunca bloqueia).
  const confirm = useConfirm();
  const blockMutation = useBlockContact();
  const unblockMutation = useUnblockContact();

  // Sincronizar arquivos arrastados com anexos
  useEffect(() => {
    if (draggedFiles.length > 0) {
      addAttachments(draggedFiles);
      clearDraggedFiles();
    }
  }, [draggedFiles, addAttachments, clearDraggedFiles]);

  // ✅ State para controlar scroll automático na primeira carga
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // ✅ Ref para forçar scroll quando usuário envia mensagem
  const shouldForceScrollRef = React.useRef(false);

  // ✅ Ref para guardar se estava no bottom ANTES da mensagem chegar
  const wasAtBottomRef = React.useRef(true);

  // ✅ Ref "congelado" - captura estado ANTES da mensagem ser adicionada
  const frozenWasAtBottomRef = React.useRef(true);

  // ✅ Flag para saber se listener já foi anexado
  const scrollListenerAttachedRef = React.useRef(false);

  // ✅ Atualizar wasAtBottomRef sempre que o scroll mudar
  useEffect(() => {
    const container = messagesContainerRef.current;

    // Se container não existe OU listener já foi anexado, não fazer nada
    if (!container || scrollListenerAttachedRef.current) return;

    scrollListenerAttachedRef.current = true;

    const handleScroll = () => {
      const threshold = 50;
      const { scrollHeight, scrollTop, clientHeight } = container;
      const atBottom = scrollHeight - scrollTop - clientHeight < threshold;
      wasAtBottomRef.current = atBottom;
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Capturar estado inicial

    return () => {
      container.removeEventListener('scroll', handleScroll);
      scrollListenerAttachedRef.current = false;
    };
  }, [messages.length]);

  // ✅ Congelar wasAtBottomRef ANTES de messages mudar (useLayoutEffect executa ANTES)
  useLayoutEffect(() => {
    frozenWasAtBottomRef.current = wasAtBottomRef.current;
  }, [messages.length]);

  // ✅ Auto-scroll inteligente para última mensagem
  useEffect(() => {
    // Caso 1: Primeira carga da conversa → sempre scroll para bottom
    if (isInitialLoad && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      setTimeout(() => {
        setIsInitialLoad(false);
        wasAtBottomRef.current = true;
        frozenWasAtBottomRef.current = true;
      }, 100);
      return;
    }

    // Caso 2: Usuário enviou mensagem → SEMPRE scroll para bottom
    if (shouldForceScrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      shouldForceScrollRef.current = false;
      wasAtBottomRef.current = true;
      frozenWasAtBottomRef.current = true;
      return;
    }

    // Caso 3: Nova mensagem recebida → scroll somente se ESTAVA no bottom ANTES
    if (!isInitialLoad && messages.length > 0 && frozenWasAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    // Caso 4: Carregando mensagens antigas → não faz scroll (usuário está lendo acima)
  }, [messages, isInitialLoad]);

  // ✅ Reset isInitialLoad quando muda de conversa
  useEffect(() => {
    setIsInitialLoad(true);
  }, [conversationId]);

  // ✅ Arch 3.4: Socket.IO auto-reset substitui o antigo sistema de mark as read
  // Removido useEffect antigo que chamava /conversation-messages/mark-as-read
  // Agora usamos apenas Socket.IO para resetar contador (ver useEffect abaixo)

  // ✅ Arch 3.4: Emitir evento conversation:open para auto-reset do contador
  useEffect(() => {
    if (socket && isConnected && conversationId) {
      // ✅ Type-safe event emission
      const openEvent: ConversationOpenEvent = {
        conversationId
      };
      socket.emit(SOCKET_EVENTS.CONVERSATION_OPEN, openEvent);

      // ✅ Type-safe listener para confirmação de reset
      const handleUnreadReset = (data: ConversationUnreadResetEvent) => {
        if (data.conversationId === conversationId) {
          // Invalida queries para atualizar UI
          queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
          queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
          // ✅ Arch 3.4: Invalida contagem de não lidas para atualizar badge
          queryClient.invalidateQueries({ queryKey: ['unread-count'] });
          queryClient.invalidateQueries({ queryKey: ['conversations', 'unread-count'], exact: false });
        }
      };

      socket.on(SOCKET_EVENTS.CONVERSATION_UNREAD_RESET, handleUnreadReset);

      // ✅ Type-safe listener para erros
      const handleError = (error: ConversationErrorEvent) => {
        console.error(`❌ [Socket.IO] Erro em ${SOCKET_EVENTS.CONVERSATION_ERROR}:`, error);
      };

      socket.on(SOCKET_EVENTS.CONVERSATION_ERROR, handleError);

      // Cleanup
      return () => {
        socket.off(SOCKET_EVENTS.CONVERSATION_UNREAD_RESET, handleUnreadReset);
        socket.off(SOCKET_EVENTS.CONVERSATION_ERROR, handleError);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]); // ✅ Emite apenas quando conversationId muda

  /**
   * Scroll para mensagem destacada na busca
   */
  useEffect(() => {
    if (highlightedMessageId && chatContainerRef.current) {
      const messageElement = document.getElementById(`message-${highlightedMessageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedMessageId]);

  /**
   * Handler para arquivos selecionados manualmente
   */
  const handleFilesSelected = (files: FileList) => {
    const filesArray = Array.from(files).map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }));
    addAttachments(filesArray);
  };

  /**
   * Handler para enviar áudio gravado
   */
  const handleSendAudio = async () => {
    if (!audioData) return;

    // ✅ Criar mensagem otimista ANTES de enviar
    const audioPreviewUrl = URL.createObjectURL(audioData.blob);

    const messageData = {
      conversationId,
      content: [{
        type: 'audio' as const,
        url: audioPreviewUrl,  // Preview local
        duration: duration,     // Duração da gravação
      }],
      // ✅ CORRIGIDO: Backend espera replyToMessageId, não parentId
      ...(replyingToMessage && {
        replyToMessageId: replyingToMessage.id,
        reply: replyingToMessage  // ✅ NOVO: Passar mensagem completa para preview otimista
      }),
    };

    const tempId = addOptimisticMessage(conversationId, messageData);
    shouldForceScrollRef.current = true; // ✅ Forçar scroll ao enviar áudio

    try {
      // Converter Blob para File — extensão TEM que casar com o conteúdo real
      // (Ogg/Opus do opus-recorder): o WebKit/Safari escolhe o demuxer pela
      // extensão da URL, e `.webm` com bytes Ogg falha com erro 4 mesmo com
      // Content-Type correto (fix iOS 2026-08-19; QuickMessageForm já fazia).
      const audioFile = new File([audioData.blob], `audio_${Date.now()}.ogg`, {
        type: audioData.blob.type || 'audio/ogg',
      });

      // ✅ Enviar para servidor e capturar resposta
      const serverMessage = await sendMessageWithFile(conversationId, audioFile);

      // ✅ Atualizar mensagem otimista com dados do servidor
      updateOptimisticMessage(tempId, serverMessage);

      toast.success('Áudio enviado!');
      chatDraftStore.clearComposer(conversationId);
      // ⚠️ Gate: só limpa áudio/reply LOCAIS se o usuário ainda está nesta
      // conversa — se já trocou para outra (envio em voo), o switch effect já
      // restaurou o composer da nova conversa e este reset apagaria o dela.
      if (conversationIdRef.current === conversationId) {
        clearAudio();

        // ✅ Limpar reply preview
        if (replyingToMessage) {
          setReplyingToMessage(null);
        }
      }
    } catch (error) {
      // ✅ Marcar mensagem otimista como falha
      markOptimisticMessageFailed(conversationId, tempId);
      toast.error('Erro ao enviar áudio');
      console.error('Error sending audio:', error);
    }
  };

  /**
   * Envio recusado (422) porque a janela de 24h da Meta fechou. Cobre o
   * caminho SÍNCRONO: nosso próprio gate recusa antes de chamar o provider,
   * então não existe `providerMessageId` e nenhum socket MESSAGE_STATUS chega
   * (esse caminho assíncrono é tratado por `useChatSocket` + `useMessagingWindow`).
   * Aqui: preserva o rascunho digitado de volta no composer (o ChatInput já
   * limpou o campo local antes de chamar `onSendMessage`), invalida a conversa
   * — para o hook (Task 12) sair de `active` e o bloco/CTA de template
   * aparecer sem esperar o `staleTime` de 30s — e mostra a mensagem do backend.
   *
   * Retorna `true` quando tratou o erro (chamador ainda deve marcar a mensagem
   * otimista como falha); `false` quando não é este caso.
   */
  const handleMessagingWindowClosedError = (err: unknown, draftText: string): boolean => {
    if (!axios.isAxiosError(err) || !err.response?.data) return false;

    const data = err.response.data as { code?: string; message?: string };
    if (data.code !== MESSAGING_WINDOW_CLOSED_CODE) return false;

    if (draftText.trim()) {
      if (conversationIdRef.current === conversationId) {
        chatInputRef.current?.setText(draftText);
      } else {
        // Usuário já trocou de conversa — preserva o rascunho da conversa original
        // direto no store, sem tocar no composer da conversa atual.
        chatDraftStore.setText(conversationId, draftText);
      }
    }
    queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    toast.error(data.message ?? 'A janela de 24 horas da Meta foi encerrada.');
    return true;
  };

  /**
   * Envio recusado (422) porque o contato está bloqueado (2026-09-01). Mesmo
   * formato do irmão acima: preserva o rascunho, invalida a conversa (para a
   * faixa aparecer) e mostra a mensagem do backend.
   */
  const handleContactBlockedError = (err: unknown, draftText: string): boolean => {
    if (!axios.isAxiosError(err) || !err.response?.data) return false;

    const data = err.response.data as { code?: string; message?: string };
    if (data.code !== CONTACT_BLOCKED_CODE) return false;

    if (draftText.trim()) {
      if (conversationIdRef.current === conversationId) {
        chatInputRef.current?.setText(draftText);
      } else {
        chatDraftStore.setText(conversationId, draftText);
      }
    }
    queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    toast.error(data.message ?? 'Este contato está bloqueado.');
    return true;
  };

  /**
   * Handler otimista para envio de mensagens
   */
  const handleSendMessage = async (message: string) => {
    // 0. Mídia de mensagem rápida "staged" — envia a URL S3 já hospedada via /send
    //    (sem novo upload), combinando com o texto revisado pelo atendente.
    //    ⚠️ O backend despacha só content[0] ao provider — texto em item
    //    separado do array NUNCA chega ao cliente. O texto digitado vai como
    //    `caption` DENTRO da mídia (mesmo formato do /send-media); áudio não
    //    suporta legenda, então o texto sai como segunda mensagem abaixo.
    if (stagedQuickMessageMedia && stagedQuickMessageMedia.length > 0) {
      const text = message.trim();
      const first = stagedQuickMessageMedia[0];
      const firstSupportsCaption =
        first !== undefined &&
        (first.type === 'image' || first.type === 'video' || first.type === 'document');

      const content: QuickMessageContent[] = stagedQuickMessageMedia.map((item, index) =>
        index === 0 && text && (item.type === 'image' || item.type === 'video' || item.type === 'document')
          ? { ...item, caption: text }
          : item,
      );

      const messageData = {
        conversationId,
        content,
        ...(replyingToMessage && {
          replyToMessageId: replyingToMessage.id,
          reply: replyingToMessage,
        }),
      };

      const tempId = addOptimisticMessage(conversationId, messageData);
      shouldForceScrollRef.current = true;
      setStagedQuickMessageMedia(null);

      try {
        const serverMessage = await sendMessageMutation.mutateAsync(messageData);
        updateOptimisticMessage(tempId, serverMessage);
        chatDraftStore.clearComposer(conversationId);
        // ⚠️ Gate: só limpa o reply preview LOCAL se o usuário ainda está nesta
        // conversa — se já trocou para outra (envio em voo), o switch effect já
        // restaurou o composer da nova conversa e este reset apagaria o dela.
        if (conversationIdRef.current === conversationId && replyingToMessage) {
          setReplyingToMessage(null);
        }
      } catch (error) {
        markOptimisticMessageFailed(conversationId, tempId);
        if (!handleMessagingWindowClosedError(error, message) && !handleContactBlockedError(error, message)) {
          toast.error('Erro ao enviar mensagem');
          console.error('Error sending quick message media:', error);
        }
        return; // mídia falhou — não encadeia a mensagem de texto
      }

      // Áudio de mensagem rápida + texto digitado: o texto segue como mensagem
      // própria (mesmo comportamento do fluxo "áudio gravado + texto").
      if (text && !firstSupportsCaption) {
        const textData = {
          conversationId,
          content: [{ type: 'text' as const, text }],
        };
        const textTempId = addOptimisticMessage(conversationId, textData);

        try {
          const serverText = await sendMessageMutation.mutateAsync(textData);
          updateOptimisticMessage(textTempId, serverText);
        } catch (error) {
          markOptimisticMessageFailed(conversationId, textTempId);
          if (!handleMessagingWindowClosedError(error, message) && !handleContactBlockedError(error, message)) {
            toast.error('Erro ao enviar mensagem');
            console.error('Error sending quick message text:', error);
          }
        }
      }

      return;
    }

    // 1. Se há áudio gravado, enviar o áudio
    if (audioData) {
      await handleSendAudio();
      // Se não há texto, retornar após enviar áudio
      if (!message.trim()) {
        return;
      }
    }

    // 2. Se há anexos pendentes, fazer upload primeiro
    if (attachments.length > 0) {
      // ✅ Criar mensagem otimista ANTES do upload para exibir na tela imediatamente
      const attachmentContent = attachments.map((attachment) => {
        const fileType = attachment.file.type;

        if (fileType.startsWith('image/')) {
          return {
            type: 'image' as const,
            url: URL.createObjectURL(attachment.file),  // Preview local
            caption: message.trim() || undefined,
          };
        } else if (fileType.startsWith('video/')) {
          return {
            type: 'video' as const,
            url: URL.createObjectURL(attachment.file),
            caption: message.trim() || undefined,
          };
        } else if (fileType.startsWith('audio/')) {
          return {
            type: 'audio' as const,
            url: URL.createObjectURL(attachment.file),
          };
        } else {
          return {
            type: 'document' as const,
            url: URL.createObjectURL(attachment.file),
            filename: attachment.file.name,
          };
        }
      });

      // Se há texto, adicionar conteúdo de texto também
      const messageContent = message.trim()
        ? [...attachmentContent, { type: 'text' as const, text: message }]
        : attachmentContent;

      const messageData = {
        conversationId,
        content: messageContent,
        // ✅ CORRIGIDO: Backend espera replyToMessageId, não parentId
        ...(replyingToMessage && {
          replyToMessageId: replyingToMessage.id,
          reply: replyingToMessage  // ✅ NOVO: Passar mensagem completa para preview otimista
        }),
      };

      // Criar mensagem otimista com anexos
      const tempId = addOptimisticMessage(conversationId, messageData);
      shouldForceScrollRef.current = true; // ✅ Forçar scroll ao enviar mensagem

      try {
        // Upload todos os anexos e capturar resposta do servidor
        // ✅ A legenda digitada e o vínculo de resposta acompanham apenas o PRIMEIRO
        //    anexo (evita repetir o mesmo texto/quote em cada mídia quando há vários).
        const caption = message.trim() || undefined;
        const replyToMessageId = replyingToMessage?.id;
        let isFirstAttachment = true;
        let serverMessage = null;
        for (const attachment of attachments) {
          if (attachment.uploadStatus === 'pending') {
            const attachmentCaption = isFirstAttachment ? caption : undefined;
            const attachmentReplyTo = isFirstAttachment ? replyToMessageId : undefined;
            isFirstAttachment = false;
            // ✅ Captura resposta do servidor (ConversationMessageResponse)
            const uploadResult = await uploadAttachment(attachment.id, attachmentCaption, attachmentReplyTo);

            // uploadResult é a resposta completa do backend
            if (uploadResult && typeof uploadResult === 'object' && uploadResult.id) {
              serverMessage = uploadResult;
            }
          }
        }

        // ✅ Atualizar mensagem otimista com dados reais do servidor
        if (serverMessage) {
          updateOptimisticMessage(tempId, serverMessage);
        }

        chatDraftStore.clearComposer(conversationId);
        // ⚠️ Gate: só limpa anexos/reply LOCAIS se o usuário ainda está nesta
        // conversa — se já trocou para outra (upload em voo), o switch effect já
        // restaurou o composer da nova conversa e este reset apagaria o dela.
        if (conversationIdRef.current === conversationId) {
          // Limpar anexos após upload
          clearAttachments();
        }

        // ✅ Mostrar toast de sucesso
        toast.success(`${attachments.length} arquivo(s) enviado(s)!`);

        // ✅ Limpar reply preview após envio bem-sucedido
        if (conversationIdRef.current === conversationId && replyingToMessage) {
          setReplyingToMessage(null);
        }
      } catch (error) {
        // Marca como falha em caso de erro
        markOptimisticMessageFailed(conversationId, tempId);
        // ⚠️ `sendMessageWithFile` sobe via XMLHttpRequest cru (progress tracking),
        // não `api`/axios — o erro chega como `Error` com só `.message` de texto,
        // sem `.response.data.code`. Não há como aplicar `handleMessagingWindowClosedError`
        // aqui sem comparar por texto (proibido — código real deve vir de `code`,
        // nunca de substring da mensagem). Envio de mídia fora da janela ainda cai
        // no fallback genérico abaixo; tratamento estruturado exigiria o backend
        // devolver `code` também no corpo de erro do endpoint `send-media` via JSON
        // que o XHR já parseia (`body.message` já é usado) — fora do escopo desta tarefa.
        // Backend devolve mensagem específica (ex: limite de tamanho do canal) — exibir
        const backendMessage = error instanceof Error && error.message && !error.message.startsWith('HTTP error')
          ? error.message
          : 'Erro ao enviar arquivo(s)';
        toast.error(backendMessage);
        console.error('Error uploading attachments:', error);
      }

      return;  // ✅ Retorna após processar anexos
    }

    // 3. Se há mensagem de texto (sem anexos), enviar normalmente
    if (message.trim()) {
      const messageData = {
        conversationId,
        content: [{ type: 'text' as const, text: message }],
        // ✅ CORRIGIDO: Backend espera replyToMessageId, não parentId
        ...(replyingToMessage && {
          replyToMessageId: replyingToMessage.id,
          reply: replyingToMessage  // ✅ NOVO: Passar mensagem completa para preview otimista
        }),
      };

      // 1. Adiciona mensagem otimista imediatamente
      const tempId = addOptimisticMessage(conversationId, messageData);
      shouldForceScrollRef.current = true; // ✅ Forçar scroll ao enviar mensagem

      try {
        // 2. Envia para o servidor
        const serverMessage = await sendMessageMutation.mutateAsync(messageData);

        // 3. Atualiza mensagem otimista com dados do servidor
        updateOptimisticMessage(tempId, serverMessage);
        chatDraftStore.clearComposer(conversationId);

        // 4. Limpa reply preview após envio bem-sucedido
        // ⚠️ Gate: só se o usuário ainda está nesta conversa — se já trocou
        // para outra (envio em voo), o switch effect já restaurou o composer
        // da nova conversa e este reset apagaria o dela.
        if (conversationIdRef.current === conversationId && replyingToMessage) {
          setReplyingToMessage(null);
        }
      } catch (error) {
        // 5. Marca como falha em caso de erro
        markOptimisticMessageFailed(conversationId, tempId);
        if (!handleMessagingWindowClosedError(error, message) && !handleContactBlockedError(error, message)) {
          toast.error('Erro ao enviar mensagem');
          console.error('Error sending message:', error);
        }
      }
    }
  };

  const handleReact = useCallback((messageId: string, emoji: string) => {
    addReaction({ messageId, emoji });
  }, [addReaction]);

  const handleRemoveReaction = useCallback((messageId: string, emoji: string) => {
    removeReaction({ messageId, emoji });
  }, [removeReaction]);

  // Handler para ações de mensagens (edit, delete, react, forward, reply)
  const handleMessageAction = (action: MessageAction, message: ConversationMessageResponse) => {
    switch (action) {
      case 'edit':
        setEditingMessage(message);
        setIsEditDialogOpen(true);
        break;

      case 'delete':
        setDeletingMessage(message);
        setIsDeleteDialogOpen(true);
        break;

      case 'react':
        // Handled by onReact/onRemoveReaction callbacks
        break;

      case 'forward':
        setForwardingMessage(message);
        setIsForwardDialogOpen(true);
        break;

      case 'reply':
        console.log('📎 [Reply] Setting reply to message:', {
          id: message.id,
          idType: typeof message.id,
          idLength: message.id?.length,
          isValidObjectId: /^[0-9a-fA-F]{24}$/.test(message.id),
          conversationId: message.conversationId,
          messagePreview: message.plainText?.substring(0, 50)
        });
        setReplyingToMessage(message);
        break;
    }
  };

  // Handler para salvar mensagem editada
  const handleSaveEditedMessage = (newContent: any[]) => {
    if (!editingMessage) return;

    editMessage({
      messageId: editingMessage.id,
      data: { content: newContent },
    });
  };

  // Handler para encaminhar mensagem para contatos
  const handleForwardMessage = async (contactIds: string[]) => {
    if (!forwardingMessage) return;

    try {
      const result = await forwardMessageAsync({
        originalMessageId: forwardingMessage.id,
        contactIds,
      });

      const successCount = result.successful.length;
      const failedCount = result.failed.length;

      if (successCount > 0 && failedCount === 0) {
        toast.success(`Mensagem encaminhada para ${successCount} contato(s) com sucesso`);
      } else if (successCount > 0 && failedCount > 0) {
        toast.success(`Encaminhado para ${successCount}. ${failedCount} falhou(aram): ${result.failed[0]?.reason ?? ''}`);
      } else {
        toast.error(`Falha ao encaminhar. ${result.failed[0]?.reason ?? 'Erro desconhecido'}`);
      }

      setIsForwardDialogOpen(false);
      setForwardingMessage(null);
    } catch (error) {
      console.error('Erro ao encaminhar mensagem:', error);
      toast.error('Erro ao encaminhar mensagem');
    }
  };

  // Envia content estruturado (localização/contato do menu de anexos) pelo
  // mesmo POST /send do texto — o backend já valida e despacha os dois tipos
  // (spec 2026-08-19, Fase 3). Sem optimistic update: o invalidate do
  // useSendMessage recarrega a lista assim que o servidor confirma.
  const handleSendContent = async (content: MessageContent[], closeDialog: () => void) => {
    setIsSendingContent(true);
    try {
      await sendMessageMutation.mutateAsync({ conversationId, content });
      closeDialog();
    } catch (error) {
      const first = content[0];
      const label = first?.type === 'location' ? 'localização' : 'contato';
      if (!handleMessagingWindowClosedError(error, '') && !handleContactBlockedError(error, '')) {
        console.error(`Erro ao enviar ${label}:`, error);
        toast.error(`Erro ao enviar ${label}`);
      }
    } finally {
      setIsSendingContent(false);
    }
  };

  // Handler para confirmar exclusão de mensagem
  const handleConfirmDelete = (deleteForEveryone: boolean) => {
    if (!deletingMessage) return;

    // deleteForEveryone=true + outbound → backend chama provider para revoke no WhatsApp.
    // Caso contrário → soft-delete local apenas (inbound ou "apagar para mim").
    deleteMessage({
      messageId: deletingMessage.id,
      deleteForEveryone: deleteForEveryone && deletingMessage.direction === 'outbound',
    });

    setIsDeleteDialogOpen(false);
    setDeletingMessage(null);
  };

  // Handler para transferir conversa
  const handleTransferConversation = (targetType: 'user' | 'team', targetId: string, notes?: string) => {
    transferConversation({
      ...(targetType === 'user' ? { toUserId: targetId } : { toTeamId: targetId }),
      notes,
    });
  };

  // Handler para finalizar conversa
  const handleFinishConversation = (reason?: string, notes?: string) => {
    closeConversation({
      reason,
      notes,
    });
  };

  // Handler para toggle mute/unmute — persiste no backend via endpoint
  const handleToggleMute = () => {
    const wasMuted = isConversationMuted;
    toggleConversationMute(
      { id: conversationId, currentlyMuted: wasMuted },
      {
        onSuccess: () => {
          toast.success(wasMuted ? 'Conversa dessilenciada' : 'Conversa silenciada');
        },
        onError: () => {
          toast.error('Falha ao atualizar preferência. Tente novamente.');
        },
      },
    );
  };

  /**
   * O menu dispara `onClick` direto e `variant: 'danger'` só pinta de vermelho —
   * e "Excluir" é o item vizinho. Sem confirmação, um toque errado bloqueia na
   * hora e apaga mensagem de cliente. O texto diz o EFEITO, não só o nome da ação.
   */
  const handleToggleBlock = async (): Promise<void> => {
    const contact = conversation?.contact;
    if (!contact) return;

    if (contact.blocked) {
      unblockMutation.mutate(contact.id);
      return;
    }

    if (!(await confirm({
      title: 'Bloquear contato',
      message: `Bloquear "${contact.name}"? As próximas mensagens dele não vão mais chegar, e nada será enviado a ele. A conversa atual será encerrada.`,
      confirmButtonText: 'Bloquear',
      variant: 'danger',
    }))) return;

    blockMutation.mutate(contact.id);
  };

  if (conversationLoading) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: 'var(--theme-card-background)' }}>
        <div className="text-center">
          <div
            className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: 'var(--theme-sidebar-active)', borderTopColor: 'transparent' }}
          ></div>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Carregando conversa...</p>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: 'var(--theme-card-background)' }}>
        <div className="text-center">
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Conversa não encontrada</p>
        </div>
      </div>
    );
  }

  // ✅ Nome e avatar dinâmicos
  const displayName = conversation.group?.name || conversation.contact?.name || conversation.subject || `Conversa #${conversation.id.slice(0, 8)}`;
  const avatarUrl = conversation.group?.picture || conversation.contact?.picture;
  const isEmailChannel = conversation.provider?.type === 'email-resend' || conversation.provider?.type === 'email-smtp';

  // A coluna CRM some inteira pra quem não tem `crm:read` — senão sobraria a legenda
  // "CRM" sozinha (o ConversationStageSelector devolve null sem essa permissão).
  const canReadCrm = hasModulePermission('crm', 'read');
  // C5 — primeiro dos três gates de `contacts:update` (header, painel, diálogo).
  const canUpdateContacts = hasModulePermission('contacts', 'update');
  const isGroupConversation = conversation.conversationType === 'group';

  // Ações mobile-only do kebab (••• — CardActionsMenu, F78): os botões standalone
  // do desktop somem <lg e são realocados pro menu (paridade com o comportamento antigo).
  const moreMenuMobileActions: CardAction[] = isMobile && isInAttendance
    ? [
        { label: 'Buscar mensagens', icon: 'search', onClick: () => setIsSearchOpen(true) },
        ...(conversation.conversationType !== 'group'
          ? ([
              { label: 'Transferir conversa', icon: 'refresh', onClick: () => setIsTransferDialogOpen(true) },
              { label: 'Finalizar conversa', icon: 'check-circle', onClick: () => setIsFinishDialogOpen(true) },
            ] satisfies CardAction[])
          : []),
        {
          label: isConversationMuted ? 'Dessilenciar' : 'Silenciar',
          icon: isConversationMuted ? 'bell' : 'bell-off',
          onClick: () => handleToggleMute(),
          disabled: isMuteToggling,
        },
      ]
    : [];

  // Ações que descem pro `···` quando a conversa fica ≤964px (spec §4, degrau 1).
  // No celular a realocação já é feita por `moreMenuMobileActions` — por isso o
  // `!isMobile`, senão "Silenciar" apareceria duas vezes no mesmo menu. `useIsMobile()`
  // corta em 1023px, o complemento exato do `@media (min-width: 1024px)` que guarda a
  // cadeia no CSS: os botões só somem da barra quando estas ações já estão no menu.
  const isHeaderNarrow = !isMobile && headerWidth > 0 && headerWidth <= CHAT_HEADER_DEMOTE_AT;
  const moreMenuDemotedActions: CardAction[] = isHeaderNarrow
    ? [
        ...(isInAttendance
          ? ([{
              label: isConversationMuted ? 'Dessilenciar' : 'Silenciar',
              icon: isConversationMuted ? 'bell' : 'bell-off',
              onClick: () => handleToggleMute(),
              disabled: isMuteToggling,
            }] satisfies CardAction[])
          : []),
        { label: 'Informações', icon: 'info', onClick: onToggleRightSidebar },
        { label: 'Fechar', icon: 'x', onClick: onClose },
      ]
    : [];

  // Bloqueio de contato (2026-09-01) — só vale na conversa individual (I3: grupo
  // nunca bloqueia). `dividerBefore` aqui herda o mesmo cálculo que "Privacidade"
  // usava sozinha — como este item passa a entrar ANTES dela na lista, o divisor
  // se move para cá e "Privacidade" (abaixo) perde o seu quando este item existe.
  const contactBlockActions: CardAction[] = canUpdateContacts && !isGroupConversation && conversation.contact
    ? ([{
        label: conversation.contact.blocked ? 'Desbloquear contato' : 'Bloquear contato',
        icon: conversation.contact.blocked ? 'unlock' : 'slash',
        variant: conversation.contact.blocked ? undefined : ('danger' as const),
        onClick: () => void handleToggleBlock(),
        disabled: blockMutation.isPending || unblockMutation.isPending,
        dividerBefore: moreMenuMobileActions.length + moreMenuDemotedActions.length > 0,
      }] satisfies CardAction[])
    : [];

  const moreMenuActions: CardAction[] = conversation.conversationType === 'group'
    ? [
        ...moreMenuMobileActions,
        ...moreMenuDemotedActions,
        {
          label: 'Privacidade',
          icon: 'lock',
          onClick: () => { setDetailsOnlyTab('privacy'); setIsDetailsModalOpen(true); },
          dividerBefore: moreMenuMobileActions.length + moreMenuDemotedActions.length > 0,
        },
        { label: 'Membros', icon: 'users', onClick: () => { setDetailsOnlyTab('members'); setIsDetailsModalOpen(true); } },
      ]
    : [
        ...moreMenuMobileActions,
        ...moreMenuDemotedActions,
        ...contactBlockActions,
        {
          label: 'Privacidade',
          icon: 'lock',
          onClick: () => { setDetailsOnlyTab('privacy'); setIsDetailsModalOpen(true); },
          dividerBefore:
            contactBlockActions.length === 0 &&
            moreMenuMobileActions.length + moreMenuDemotedActions.length > 0,
        },
        { label: 'Criar lead', icon: 'briefcase', onClick: () => setIsCreateLeadOpen(true) },
        { label: 'Criar evento', icon: 'calendar', onClick: () => setIsCreateEventOpen(true) },
        ...(channelSupportsTemplates
          ? ([{ label: 'Enviar template', icon: 'file-text', onClick: () => setIsSendTemplateOpen(true) }] satisfies CardAction[])
          : []),
        ...(hasAgent
          ? ([{
              label: isGolden ? 'Desmarcar exemplo' : 'Salvar como exemplo',
              icon: 'star',
              onClick: toggleGolden,
              disabled: markGoldenMutation.isPending || unmarkGoldenMutation.isPending,
            }] satisfies CardAction[])
          : []),
      ];

  return (
    <div
      ref={chatContainerRef}
      className="flex-1 flex flex-col relative h-full overflow-hidden"
      style={{ backgroundColor: 'var(--theme-card-background)' }}
    >
      {/* Drag Overlay */}
      <DragOverlay isVisible={isDragging} />

      {/* HEADER */}
      <div
        ref={headerRef}
        data-testid="chat-header"
        className="chat-header border-b flex items-center flex-shrink-0"
        style={{
          borderColor: 'var(--theme-card-border)',
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          // mockup `.th`: 56 de altura, 14 de respiro lateral, 10 entre os blocos
          padding: '0 14px',
          gap: 10,
          // nada invade a zona de ações; tooltip e menus são portais, não são clipados
          overflow: 'hidden',
        }}
      >
        {/* Voltar — só mobile (thread é slide-over sobre a lista, mockup m-th-head) */}
        <button
          onClick={onClose}
          className="lg:hidden -ml-1 w-[34px] h-[34px] rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
          style={{ color: 'var(--theme-sidebar-text)' }}
          aria-label="Voltar"
          data-testid="chat-mobile-back"
        >
          <Icon name="chevron-left" className="w-[22px] h-[22px]" />
        </button>

        {/* Avatar (mockup: 38 mobile / 40 desktop) */}
        <Avatar className="w-[38px] h-[38px] lg:w-10 lg:h-10 flex-shrink-0" isSquircle>
          <AvatarImage src={avatarUrl} alt={displayName} isSquircle />
          <AvatarFallback isSquircle>
            <span className="text-white font-semibold">{displayName.charAt(0) || 'C'}</span>
          </AvatarFallback>
        </Avatar>

        {/* Colunas rotuladas — NOME / CRM / CLIENTE / TAGS (spec §3, mockup variante 01) */}
        <div className="chat-hdr-cols" style={{ userSelect: 'none' }}>
          {/* ── Identidade: sem legenda de propósito, o nome ocupa esse lugar ── */}
          <div className="chat-hdr-col chat-hdr-col--ident flex">
            <div className="chat-hdr-row">
              <h3
                className="font-bold text-[15px] lg:text-[14px] truncate min-w-0"
                style={{ color: 'var(--theme-card-text)', lineHeight: '16px', letterSpacing: '-0.02em' }}
                title={displayName}
              >
                {displayName}
              </h3>
              {isGolden && (
                <span
                  data-testid="chatbox-golden-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: '#fef3c7', color: '#b45309' }}
                  title="Conversa salva como exemplo — usada em regression test de prompts novos"
                >
                  <Icon name="star" className="w-3 h-3" />
                  EXEMPLO
                </span>
              )}
            </div>
            <div className="chat-hdr-row">
              {/* Celular: "Ativa" (mockup m-th-head) */}
              {!isGroupConversation && (
                <span
                  className="lg:hidden text-[11px] font-semibold"
                  style={{
                    color: conversation.status === 'active'
                      ? 'var(--theme-success-color)'
                      : 'var(--theme-muted-text)',
                  }}
                >
                  {conversation.status === 'active' ? 'Ativa'
                    : conversation.status === 'waiting' ? 'Em espera'
                    : conversation.status === 'closed' ? 'Finalizada'
                    : conversation.status}
                </span>
              )}
              {/* E-mail: o assunto ocupa a linha de baixo. A caixa só tem DUAS linhas de
                  22px — assunto e telefone juntos estouravam os 56px e agora seriam
                  clipados pelo overflow:hidden. */}
              {isEmailChannel && conversation.subject && (
                <span
                  className="hidden lg:inline truncate"
                  style={{ color: 'var(--theme-muted-text)', fontSize: 12.5, lineHeight: '16px' }}
                  title={conversation.subject}
                >
                  {conversation.subject}
                </span>
              )}
              {/* Demais canais: ícone do canal + telefone (mockup `.ph`: 12.5px, gap 5) */}
              {!isEmailChannel && conversation.contact?.phone && (
                <span
                  className="hidden lg:inline-flex items-center min-w-0"
                  style={{ gap: 5 }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (conversation.contact?.phone) {
                      navigator.clipboard.writeText(conversation.contact.phone);
                      toast.success('Número copiado para a área de transferência');
                    }
                  }}
                >
                  {conversation.provider?.type && (
                    <IntegrationIcon id={conversation.provider.type} size={14} variant="brand" />
                  )}
                  <PrivacyText
                    className="truncate"
                    style={{ color: 'var(--theme-muted-text)', fontSize: 12.5, lineHeight: '16px' }}
                    title={formatPhoneNumber(conversation.contact.phone)}
                  >
                    {formatPhoneNumber(conversation.contact.phone)}
                  </PrivacyText>
                </span>
              )}
              {/* Grupo: quantidade de membros */}
              {isGroupConversation && conversation.members && conversation.members.length > 0 && (
                <span style={{ color: 'var(--theme-muted-text)', fontSize: 12.5, lineHeight: '16px' }}>
                  {conversation.members.length} {conversation.members.length === 1 ? 'membro' : 'membros'}
                </span>
              )}
              {/* Individual sem telefone e sem assunto */}
              {!isGroupConversation && !conversation.contact?.phone && !(isEmailChannel && conversation.subject) && (
                <span
                  className="hidden lg:inline"
                  style={{ color: 'var(--theme-muted-text)', fontSize: 12.5, lineHeight: '16px' }}
                >
                  {conversation.status === 'active' ? 'Ativa' : conversation.status}
                </span>
              )}
            </div>
          </div>

          {/* ── CRM ── */}
          {/* `!isMobile` além do `hidden lg:flex`: abaixo de 1024 a coluna já era
              invisível, mas continuava NO DOM — e a faixa M1 monta os MESMOS
              componentes, duplicando `stage-selector-trigger`, `chat-customer-trigger`
              e `chat-tags-trigger`. `useIsMobile()` (max-width 1023) é o complemento
              exato do `lg:`, então em qualquer largura existe uma anatomia só. */}
          {!isGroupConversation && !isMobile && canReadCrm && (
            <>
              <span className="chat-hdr-sep hidden lg:block" />
              <div className="chat-hdr-col chat-hdr-col--crm hidden lg:flex" data-testid="chat-header-col-crm">
                <div className="chat-hdr-row chat-hdr-labelrow">
                  <span className="chat-hdr-label" data-testid="chat-header-label-crm">CRM</span>
                </div>
                <div className="chat-hdr-row">
                  <ConversationStageSelector conversation={conversation} />
                </div>
              </div>
            </>
          )}

          {/* ── Cliente ── */}
          {!isGroupConversation && !isMobile && (
            <>
              <span className="chat-hdr-sep chat-hdr-sep--customer hidden lg:block" />
              <div className="chat-hdr-col chat-hdr-col--customer hidden lg:flex" data-testid="chat-header-col-customer">
                <div className="chat-hdr-row chat-hdr-labelrow">
                  <span className="chat-hdr-label" data-testid="chat-header-label-customer">Cliente</span>
                </div>
                <div className="chat-hdr-row">
                  <ConversationCustomerSelector conversation={conversation} variant="desktop" />
                </div>
              </div>
            </>
          )}

          {/* ── Tags ── */}
          {!isGroupConversation && !isMobile && (
            <>
              <span className="chat-hdr-sep chat-hdr-sep--tags hidden lg:block" />
              <div className="chat-hdr-col chat-hdr-col--tags hidden lg:flex" data-testid="chat-header-col-tags">
                <div className="chat-hdr-row chat-hdr-labelrow">
                  <span className="chat-hdr-label" data-testid="chat-header-label-tags">Tags</span>
                </div>
                <button
                  type="button"
                  data-testid="chat-tags-trigger"
                  onClick={() => { if (canUpdateContacts && headerContactId) setIsTagsDialogOpen(true); }}
                  disabled={!canUpdateContacts || !headerContactId}
                  title={canUpdateContacts ? 'Gerenciar tags do contato' : 'Tags do contato'}
                  aria-label="Gerenciar tags do contato"
                  className="chat-hdr-row"
                  style={{ gap: 5, cursor: canUpdateContacts && headerContactId ? 'pointer' : 'default' }}
                >
                  {contactTags.length === 0 ? (
                    !canUpdateContacts ? (
                      // Sem `contacts:update` não há o que clicar, mas a legenda "TAGS"
                      // sozinha, sem nada embaixo, parecia coluna quebrada. O travessão é
                      // o mesmo vazio explícito do "Nenhuma tag adicionada" do painel.
                      <span style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}>—</span>
                    ) : (
                    <span
                      className="chat-hdr-tag chat-hdr-tag-solo inline-flex items-center rounded-full"
                      style={{
                        height: 20,
                        padding: '0 7px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--theme-muted-text)',
                        border: '1px dashed var(--theme-card-border)',
                      }}
                    >
                      + Tag
                    </span>
                    )
                  ) : (
                    <>
                      {contactTags.map((tag: string, index: number) => (
                        <span
                          key={tag}
                          className={`chat-hdr-tag inline-flex items-center rounded-full ${index > 0 ? 'chat-hdr-tag-extra' : ''} ${contactTags.length === 1 ? 'chat-hdr-tag-solo' : ''}`}
                          style={{
                            height: 20,
                            padding: '0 8px',
                            fontSize: 11,
                            fontWeight: 600,
                            // Cinza neutro do mockup (`.tg` = --chip-bg/--text-secondary).
                            // Lilás é EXCLUSIVO da pílula do CRM — tag lilás dilui o sinal.
                            backgroundColor: 'var(--hdr-chip-bg)',
                            color: 'var(--hdr-chip-fg)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                      {contactTags.length > 1 && (
                        <span
                          className="chat-hdr-tag chat-hdr-tag-count items-center rounded-full"
                          style={{
                            height: 20,
                            padding: '0 8px',
                            fontSize: 11,
                            fontWeight: 600,
                            // Cinza neutro do mockup (`.tg` = --chip-bg/--text-secondary).
                            // Lilás é EXCLUSIVO da pílula do CRM — tag lilás dilui o sinal.
                            backgroundColor: 'var(--hdr-chip-bg)',
                            color: 'var(--hdr-chip-fg)',
                            whiteSpace: 'nowrap',
                          }}
                          title={contactTags.join(', ')}
                        >
                          +{contactTags.length - 1}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isInAttendance && canStartAttendance ? (
            /* ✅ Botão "Iniciar atendimento" quando não está atendendo e userRole é attendant */
            <Button
              onClick={handleStartAttendance}
              disabled={startAttendanceMutation.isPending}
              variant="primary"
              size="md"
              icon={'user-plus'}
              iconPosition="left"
              loading={startAttendanceMutation.isPending}
            >
              {startAttendanceMutation.isPending ? (
                'Iniciando...'
              ) : (
                <>
                  <span className="lg:hidden">Iniciar</span>
                  <span className="hidden lg:inline">Iniciar atendimento</span>
                </>
              )}
            </Button>
          ) : isInAttendance ? (
            /* ✅ Opções completas quando está atendendo */
            <>
              {/* Botões de Ligar e Vídeo - Comentados temporariamente */}
              {/* <button
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Ligar"
              >
                <FiPhone className="w-5 h-5" />
              </button>
              <button
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Vídeo"
              >
                <FiVideo className="w-5 h-5" />
              </button> */}

              {/* Toggle AI Agent Status - Apenas se conversa tiver agente de IA */}
              {conversation.agentId && (
                <Tooltip content={conversation.agentStatus === 'active' ? 'Desativar Agente IA' : 'Ativar Agente IA'} side="bottom">
                  <button
                    onClick={handleToggleAgentStatus}
                    disabled={toggleAgentStatusMutation.isPending}
                    className="w-[34px] h-[34px] rounded-lg flex items-center justify-center transition-colors"
                    style={{
                      color: conversation.agentStatus === 'active' ? '#8b5cf6' : '#9ca3af',
                      opacity: toggleAgentStatusMutation.isPending ? 0.5 : 1,
                      cursor: toggleAgentStatusMutation.isPending ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!toggleAgentStatusMutation.isPending) {
                        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    data-testid="chat-agent-toggle"
                  >
                    <Icon name="cpu" className="h-[18px] w-[18px]" />
                  </button>
                </Tooltip>
              )}

              <Tooltip content="Buscar mensagens" side="bottom">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="w-[34px] h-[34px] rounded-lg hidden lg:flex items-center justify-center transition-colors"
                  style={{
                    backgroundColor: isSearchOpen ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    color: isSearchOpen ? 'var(--theme-sidebar-active)' : 'var(--theme-sidebar-text)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSearchOpen) {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSearchOpen ? 'rgba(139, 92, 246, 0.1)' : 'transparent';
                  }}
                  data-testid="chat-search-toggle"
                >
                  <Icon name="search" className="h-[18px] w-[18px]" />
                </button>
              </Tooltip>

              <span className="w-px h-5 mx-1 self-center hidden lg:block" style={{ backgroundColor: 'var(--theme-card-border)' }} />

              {/* Botões Transferir e Finalizar - Apenas para conversas individuais e IA (não grupos) */}
              {conversation.conversationType !== 'group' && (
                <>
                  <Tooltip content="Transferir conversa" side="bottom">
                    <button
                      data-testid="chat-transfer-button"
                      onClick={() => setIsTransferDialogOpen(true)}
                      className="w-[34px] h-[34px] rounded-lg hidden lg:flex items-center justify-center transition-colors"
                      style={{ color: 'var(--theme-sidebar-text)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="refresh" className="h-[18px] w-[18px]" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Finalizar conversa" side="bottom">
                    <button
                      onClick={() => setIsFinishDialogOpen(true)}
                      className="w-[34px] h-[34px] rounded-lg hidden lg:flex items-center justify-center transition-colors"
                      style={{ color: 'var(--theme-sidebar-text)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      data-testid="chat-finish-button"
                    >
                      <Icon name="check-circle" className="h-[18px] w-[18px]" />
                    </button>
                  </Tooltip>
                </>
              )}

              <Tooltip content={isConversationMuted ? 'Dessilenciar' : 'Silenciar'} side="bottom">
                <button
                  onClick={() => handleToggleMute()}
                  disabled={isMuteToggling}
                  className="chat-hdr-demote w-[34px] h-[34px] rounded-lg hidden lg:flex items-center justify-center transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: isConversationMuted ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
                    color: isConversationMuted ? 'var(--theme-warning-color)' : 'var(--theme-sidebar-text)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isConversationMuted) {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isConversationMuted ? 'rgba(251, 191, 36, 0.1)' : 'transparent';
                  }}
                  data-testid="chat-mute-toggle"
                >
                  {isConversationMuted ? (
                    <Icon name="bell-off" className="h-[18px] w-[18px]" />
                  ) : (
                    <Icon name="bell" className="h-[18px] w-[18px]" />
                  )}
                </button>
              </Tooltip>
            </>
          ) : null}
          {isInAttendance && (
            <span className="w-px h-5 mx-1 self-center hidden lg:block" style={{ backgroundColor: 'var(--theme-card-border)' }} />
          )}
          <Tooltip content="Informações" side="bottom">
            <button
              data-testid="chat-toggle-details"
              onClick={onToggleRightSidebar}
              className="chat-hdr-demote w-[34px] h-[34px] rounded-lg flex items-center justify-center transition-colors"
              style={{
                backgroundColor: rightSidebarOpen ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                color: rightSidebarOpen ? 'var(--theme-sidebar-active)' : 'var(--theme-sidebar-text)'
              }}
              onMouseEnter={(e) => {
                if (!rightSidebarOpen) {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = rightSidebarOpen ? 'rgba(139, 92, 246, 0.1)' : 'transparent';
              }}
            >
              <Icon name="info" className="h-[18px] w-[18px]" />
            </button>
          </Tooltip>
          {/* Menu "Mais opções" (•••) — CardActionsMenu shared (F78): dropdown Radix
              no desktop, ActionSheet no mobile; ações montadas em moreMenuActions */}
          <CardActionsMenu
            data-testid="chatbox-more-menu"
            actions={moreMenuActions}
          />
          <Tooltip content="Fechar" side="bottom">
            <button
              onClick={onClose}
              className="chat-hdr-demote w-[34px] h-[34px] rounded-lg hidden lg:flex items-center justify-center transition-colors"
              style={{ color: 'var(--theme-sidebar-text)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              data-testid="chat-close-button"
            >
              <Icon name="x" className="h-[18px] w-[18px]" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* FAIXA DE CONTEXTO — só celular (M1). Some sozinha em grupo e no ≥lg.
          `isMobile` aqui é o par do `!isMobile` das 3 colunas: uma anatomia por vez. */}
      {isMobile && <ConversationContextStrip conversation={conversation} />}

      {/* MESSAGE SEARCH */}
      <MessageSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSearchChange={setSearchQuery}
        totalResults={totalResults}
        currentResultIndex={currentResultIndex}
        onNavigateNext={navigateNext}
        onNavigatePrev={navigatePrev}
        initialValue={initialSearchTerm}
      />

      {/* Tags do contato — MESMO diálogo do painel direito (spec §9: reusar, não duplicar).
          O salvar invalida ['contact', contactId] E ['contact', contactId, 'tags'] (a chave
          do useContactTags), então a coluna do header reflete sozinha. */}
      {headerContactId && (
        <TagsDialog
          contactId={headerContactId}
          contactName={displayName}
          isOpen={isTagsDialogOpen}
          onClose={() => setIsTagsDialogOpen(false)}
        />
      )}

      {/* MESSAGES AREA
          `overflow-x-hidden` é rede de segurança: sem ele, `overflow-y-auto` sozinho
          faz o eixo X computar para `auto` e qualquer conteúdo largo transforma a
          conversa num scroller horizontal (bug 2026-08-19 no mobile). */}
      <div
        ref={messagesContainerRef}
        data-testid="chat-message-list"
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div
                className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-2"
                style={{ borderColor: 'var(--theme-sidebar-active)' }}
              ></div>
              <p
                className="text-sm"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Carregando mensagens...
              </p>
            </div>
          </div>
        ) : displayMessages && displayMessages.length > 0 ? (
          <div className="space-y-2">
            {/* ✅ Trigger point para carregar mensagens antigas (no topo) */}
            {hasNextPage && (
              <div className="py-4 flex items-center justify-center">
                {isFetchingNextPage ? (
                  <div
                    className="flex items-center gap-2"
                    style={{ color: 'var(--theme-sidebar-active)' }}
                  >
                    <div
                      className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: 'var(--theme-sidebar-active)' }}
                    ></div>
                    <span className="text-sm">Carregando mensagens antigas...</span>
                  </div>
                ) : (
                  <button
                    onClick={() => fetchNextPage()}
                    className="text-sm font-medium"
                    style={{ color: 'var(--theme-sidebar-active)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    Carregar mensagens antigas
                  </button>
                )}
              </div>
            )}

            {/* ✅ Lista de mensagens com separadores de data */}
            {displayMessages.map((message, index) => {
              const messageDate = getMessageDate(message);
              const previousMessage = index > 0 ? displayMessages[index - 1] : null;
              const previousDate = previousMessage ? getMessageDate(previousMessage) : null;

              // Mostrar separador se é a primeira mensagem ou se a data mudou
              const showDateSeparator = index === 0 || (previousDate && !isSameDay(messageDate, previousDate));

              return (
                <div key={message.id}>
                  {/* Separador de data */}
                  {showDateSeparator && (
                    <DateSeparator date={messageDate} />
                  )}

                  {/* Mensagem */}
                  <div id={`message-${message.id}`}>
                    <ChatMessage
                      message={message}
                      isOwn={message.direction === 'outbound'}
                      showAvatar={true}
                      onAction={handleMessageAction}
                      onReact={handleReact}
                      onRemoveReaction={handleRemoveReaction}
                      onImageClick={handleImageClick}
                      isHighlighted={highlightedMessageId === message.id}
                      currentUserId={currentUserId}
                      channelId={conversationChannelId}
                      channelSupportsTemplates={channelSupportsTemplates}
                      channelSupportsEdit={channelSupportsEdit}
                    />
                  </div>
                </div>
              );
            })}
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 border-2"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)'
                }}
              >
                <span className="text-2xl">💬</span>
              </div>
              <h4
                className="font-semibold mb-1"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Nenhuma mensagem
              </h4>
              <p
                className="text-sm"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Envie a primeira mensagem para iniciar a conversa
              </p>
            </div>
          </div>
        )}
      </div>

      {/* FILE PREVIEW AREA - Apenas se o composer estiver liberado (janela aberta) */}
      {composerState === 'active' && attachments.length > 0 && (
        <div
          className="border-t p-4 flex-shrink-0"
          style={{
            borderColor: 'var(--theme-card-border)',
            backgroundColor: 'var(--theme-page-background)'
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h4
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Arquivos anexados ({attachments.length})
            </h4>
            <Tooltip content="Descartar arquivos">
              <button
                onClick={clearAttachments}
                className="p-1 rounded-lg transition-colors"
                style={{ color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  e.currentTarget.style.color = 'var(--theme-card-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--theme-muted-text)';
                }}
                aria-label="Descartar arquivos"
                data-testid="chat-attachments-clear"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </Tooltip>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {attachments.map((attachment) => (
              <FilePreview
                key={attachment.id}
                file={attachment}
                onRemove={removeAttachment}
                onRetry={uploadAttachment}
                formatFileSize={formatFileSize}
              />
            ))}
          </div>
        </div>
      )}

      {/* REPLY PREVIEW - Apenas se o composer estiver liberado (janela aberta) */}
      {composerState === 'active' && replyingToMessage && (
        <div className="flex-shrink-0">
          <ReplyPreview
            message={replyingToMessage}
            onCancel={() => setReplyingToMessage(null)}
          />
        </div>
      )}

      {/* AUDIO RECORDING PREVIEW - Apenas se o composer estiver liberado (janela aberta) */}
      {composerState === 'active' && (isRecording || audioData) && (
        <div className="flex-shrink-0">
          <AudioRecordingPreview
            isRecording={isRecording}
            duration={formatDuration(duration)}
            audioUrl={audioData?.url}
            onStop={stopRecording}
            onCancel={cancelRecording}
            onDelete={clearAudio}
          />
        </div>
      )}

      {/* QUICK MESSAGE STAGED MEDIA PREVIEW - Apenas se o composer estiver liberado (janela aberta) */}
      {composerState === 'active' && stagedQuickMessageMedia && stagedQuickMessageMedia.length > 0 && (
        <QuickMessageStagedPreview
          media={stagedQuickMessageMedia}
          onClear={() => setStagedQuickMessageMedia(null)}
        />
      )}

      {/* AVISO DE JANELA FECHANDO - Fica acima da sendbar, que continua liberada (só bloqueia em window_closed) */}
      {showWarning && reopenWith && (
        <div className="flex-shrink-0">
          <MessagingWindowWarning reopenWith={reopenWith} remainingLabel={remainingLabel} />
        </div>
      )}

      {/* INPUT AREA - Apenas se o composer estiver liberado (janela de mensageria aberta) */}
      {composerState === 'active' ? (
        <div className="flex-shrink-0">
          <ChatInput
            key={conversationId}
            ref={chatInputRef}
            conversationId={conversationId}
            onSendMessage={handleSendMessage}
            onFilesSelected={handleFilesSelected}
            onVoiceRecord={startRecording}
            onOpenQuickMessages={() => setIsQuickMessagesOpen(true)}
            quickMessages={quickMessages}
            onSelectQuickMessage={handleInsertQuickMessage}
            onOpenLocationPicker={() => setIsLocationPickerOpen(true)}
            onOpenContactPicker={() => setIsContactPickerOpen(true)}
            {...(channelSupportsPoll ? { onOpenPollCreator: () => setIsPollCreatorOpen(true) } : {})}
            disabled={isUploading || isRecording}
            hasAttachments={
              attachments.length > 0 || !!audioData || !!(stagedQuickMessageMedia && stagedQuickMessageMedia.length > 0)
            }
            placeholder={
              isRecording
                ? 'Gravando áudio...'
                : isUploading
                ? 'Enviando arquivos...'
                : undefined
            }
          />
        </div>
      ) : (
        /* ✅ Mensagem quando o composer não está liberado (janela fechada / aguardando / finalizada) */
        <div
          className="flex-shrink-0"
          style={{ borderTop: '1px solid var(--theme-card-border)', backgroundColor: 'var(--theme-surface-sunken)' }}
        >
          {composerState === 'contact_blocked' ? (
            <ContactBlockedBlock
              onUnblock={() => unblockMutation.mutate(conversation.contact!.id)}
              isUnblocking={unblockMutation.isPending}
              canUnblock={canUpdateContacts}
            />
          ) : composerState === 'window_closed' && reopenWith ? (
            <MessagingWindowBlock
              reopenWith={reopenWith}
              onSendTemplate={() => setIsSendTemplateOpen(true)}
            />
          ) : conversation.status === 'waiting' ? (
            <div
              className="flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium"
              style={{ backgroundColor: '#fffbeb', color: '#b45309' }}
              data-testid="chat-composer-waiting"
            >
              <span
                className="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
                style={{ backgroundColor: '#f59e0b' }}
              ></span>
              <span>
                {canStartAttendance
                  ? 'Conversa aguardando atendimento. Clique em "Iniciar atendimento" para responder.'
                  : 'Conversa aguardando atendimento.'}
              </span>
            </div>
          ) : conversation.status === 'closed' ? (
            <div
              className="flex items-center justify-center gap-2 px-4 py-3.5 text-sm"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="chat-composer-closed"
            >
              <Icon name="check-circle" className="w-4 h-4 flex-shrink-0" />
              <span>Conversa finalizada. Não é possível enviar mensagens.</span>
            </div>
          ) : (
            <div
              className="flex items-center justify-center gap-2 px-4 py-3.5 text-sm"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="chat-composer-inactive"
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-muted-text)' }}
              ></span>
              <span>Conversa não está ativa. Não é possível enviar mensagens.</span>
            </div>
          )}
        </div>
      )}

      {/* EDIT MESSAGE DIALOG */}
      {editingMessage && (
        <EditMessageDialog
          message={editingMessage}
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingMessage(null);
          }}
          onSave={handleSaveEditedMessage}
          isLoading={isEditingMessage}
        />
      )}

      {/* PICKERS DO MENU DE ANEXOS — localização e contato (spec 2026-08-19) */}
      <SendLocationDialog
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        onSend={(content) => { void handleSendContent(content, () => setIsLocationPickerOpen(false)); }}
        isLoading={isSendingContent}
      />
      <SendPollDialog
        isOpen={isPollCreatorOpen}
        onClose={() => setIsPollCreatorOpen(false)}
        onSend={(content) => { void handleSendContent(content, () => setIsPollCreatorOpen(false)); }}
        isLoading={isSendingContent}
      />
      <SendContactDialog
        isOpen={isContactPickerOpen}
        onClose={() => setIsContactPickerOpen(false)}
        onSend={(content) => { void handleSendContent(content, () => setIsContactPickerOpen(false)); }}
        isLoading={isSendingContent}
      />

      {/* FORWARD MESSAGE DIALOG */}
      {forwardingMessage && (
        <ForwardMessageDialog
          message={forwardingMessage}
          {...(conversation?.channelId ? { channelId: conversation.channelId } : {})}
          isOpen={isForwardDialogOpen}
          onClose={() => {
            setIsForwardDialogOpen(false);
            setForwardingMessage(null);
          }}
          onForward={handleForwardMessage}
          isLoading={false}
        />
      )}

      {/* DELETE MESSAGE DIALOG */}
      {deletingMessage && (
        <DeleteMessageDialog
          message={deletingMessage}
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setDeletingMessage(null);
          }}
          onConfirm={handleConfirmDelete}
          isLoading={isDeletingMessage}
          canDeleteForEveryone={deletingMessage.direction === 'outbound'}
        />
      )}

      {/* TRANSFER CHAT DIALOG */}
      <TransferConversationDialog
        conversationId={conversationId}
        conversationSubject={conversation?.subject}
        isOpen={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        onTransfer={handleTransferConversation}
        isLoading={isTransferring}
      />

      {/* FINISH CHAT DIALOG */}
      <FinishChatDialog
        conversationId={conversationId}
        conversationSubject={conversation?.subject}
        isOpen={isFinishDialogOpen}
        onClose={() => setIsFinishDialogOpen(false)}
        onFinish={handleFinishConversation}
        isLoading={isClosing}
      />

      {/* IMAGE VIEWER */}
      <ImageViewer
        images={allImages}
        initialIndex={selectedImageIndex}
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
      />

      {/* CREATE LEAD DRAWER */}
      <LeadFormDrawer
        isOpen={isCreateLeadOpen}
        onClose={() => setIsCreateLeadOpen(false)}
        mode="create"
        onSuccess={() => setIsCreateLeadOpen(false)}
        initialContact={conversation?.contactId ? {
          id: conversation.contactId,
          name: conversation.contact?.name || '',
          phone: conversation.contact?.phone || undefined,
        } : null}
      />

      {/* CREATE EVENT MODAL */}
      <EventFormModal
        isOpen={isCreateEventOpen}
        onClose={() => setIsCreateEventOpen(false)}
        onSuccess={() => {
          setIsCreateEventOpen(false);
          toast.success('Evento criado com sucesso!');
        }}
        calendarIntegrations={calendarIntegrations}
        initialContact={
          conversation?.contact?.id
            ? {
                id: conversation.contact.id,
                name: conversation.contact.name,
                ...(conversation.contact.phone ? { phone: conversation.contact.phone } : {}),
              }
            : null
        }
      />

      {/* SEND TEMPLATE MODAL */}
      {isSendTemplateOpen && conversationChannelId && (
        <SendTemplateModal
          isOpen={isSendTemplateOpen}
          onClose={() => setIsSendTemplateOpen(false)}
          conversationId={conversationId}
          channelId={conversationChannelId}
        />
      )}

      {/* CONVERSATION DETAILS MODAL */}
      {isDetailsModalOpen && (
        <ConversationDetailsModal
          conversationId={conversationId}
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          onlyTab={detailsOnlyTab}
        />
      )}

      {/* QUICK MESSAGES DRAWER */}
      <QuickMessagesDrawer
        isOpen={isQuickMessagesOpen}
        onClose={() => setIsQuickMessagesOpen(false)}
        onInsert={handleInsertQuickMessage}
      />

    </div>
  );
};

export default ChatBox;
